/**
 * Pure validation helpers for the project creation wizard and Projects Hub.
 *
 * Rules enforce WHO can write (`isAdmin() || isDirection() || isFinance()` on
 * `projects/{id}`). The UI enforces VALUE INVARIANTS (code regex, uniqueness,
 * team composition) and computes the "is this project mine?" predicate that
 * mirrors `firestore.rules` `isAssignedToProject`.
 *
 * Keep this file dependency-free (no React, no Firestore).
 */

import type {
  Project,
  Role,
  User,
} from '@altyor-kronos/shared-types';

// Mirror of firestore.rules `AUTO_ASSIGNED_ROLES`. Keep these in sync — if a role
// is added here, the corresponding rule helper `isAutoAssigned` must be updated.
// `admin` is included here (rules already had isAdmin() in isAssignedToProject)
// so that admin users appear in "My Projects" and access Kronos + Milestones tabs.
const AUTO_ASSIGNED: ReadonlySet<Role> = new Set(['finance', 'rd_manager', 'direction', 'quality', 'admin']);

const PROJECT_CODE_RE = /^P\d{4,5}$/;

export function normalizeProjectCode(raw: string): string {
  return raw.trim().toUpperCase();
}

export function validateProjectCode(raw: string): boolean {
  return PROJECT_CODE_RE.test(normalizeProjectCode(raw));
}

/**
 * Firestore cannot enforce cross-document uniqueness. We check client-side.
 * Small race window acceptable for an admin/direction/finance-only action.
 */
export function isProjectCodeUnique(
  candidate: string,
  existing: readonly { id: string; code: string }[],
  ignoreId?: string,
): boolean {
  const normalized = normalizeProjectCode(candidate);
  if (normalized === '') return true;
  return !existing.some(
    (p) => p.id !== ignoreId && normalizeProjectCode(p.code) === normalized,
  );
}

/**
 * Suggests the next available project code in PYYXXX format for a given year.
 * Finds the highest existing code for the year prefix and increments by one.
 */
export function suggestNextCode(
  existing: readonly { id: string; code: string }[],
  year?: number,
): string {
  const yy = String(year ?? new Date().getFullYear()).slice(-2);
  const prefix = `P${yy}`;
  const codes = existing
    .map((p) => p.code)
    .filter((c) => c.startsWith(prefix))
    .map((c) => parseInt(c.slice(prefix.length), 10))
    .filter((n) => !isNaN(n));
  const max = codes.length > 0 ? Math.max(...codes) : 0;
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
}

/**
 * Mirror of `isAssignedToProject` in firestore.rules.
 *
 * A project is "mine" for welcome-page purposes if the user is in its
 * team.managers or allMemberUids, OR holds an auto-assigned role (finance,
 * rd_manager, direction, quality, admin).
 */
export function isAssignedToMe(user: User, project: Project): boolean {
  if (project.team?.managers?.includes(user.uid)) return true;
  if (project.allMemberUids?.includes(user.uid)) return true;
  return user.roles.some((r) => AUTO_ASSIGNED.has(r));
}

// ─── Wizard step validation ──────────────────────────────────────────

export interface IdentityStepValues {
  code: string;
  name: string;
}

export interface TeamStepValues {
  managers: string[];
  rdLead: string[];
  rdMeca: string[];
  rdElec: string[];
  testBench: string[];
  dft: string[];
  supplyChain: string[];
  quality: string[];
  bizdev: string[];
  marketing: string[];
  contributors: string[];
}

export type FormErrors<T> = Partial<Record<keyof T, string>>;

export function validateIdentityStep(v: IdentityStepValues): FormErrors<IdentityStepValues> {
  const errors: FormErrors<IdentityStepValues> = {};
  if (!validateProjectCode(v.code)) {
    errors.code = 'Invalid project code';
  }
  if (!v.name || v.name.trim() === '') {
    errors.name = 'Name is required';
  }
  return errors;
}

export function emptyTeamValues(): TeamStepValues {
  return {
    managers: [], rdLead: [], rdMeca: [], rdElec: [], testBench: [],
    dft: [], supplyChain: [], quality: [], bizdev: [], marketing: [], contributors: [],
  };
}

export function validateTeam(v: TeamStepValues): FormErrors<TeamStepValues> {
  const errors: FormErrors<TeamStepValues> = {};
  if (v.managers.length === 0) {
    errors.managers = 'At least one project manager is required';
  }
  return errors;
}

export interface LinksStepValues {
  sharepointLink: string;
  teamsLink: string;
  customerWebsite: string;
}

/**
 * URLs are optional — empty string is fine. When present, we require a
 * parseable URL with a protocol to catch obvious paste mistakes (e.g. a bare
 * "sharepoint.com/…" without `https://`). Firestore stores the raw string;
 * the UI renders it via `<a>` and relies on the browser to dereference.
 */
/**
 * Un lien http(s) parseable. Extrait de `validateLinksStep` pour que les liens
 * QMS des fiches de validation appliquent exactement la même règle — un seul
 * endroit décide de ce qu'est une URL acceptable.
 */
export function isHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function validateLinksStep(v: LinksStepValues): FormErrors<LinksStepValues> {
  const errors: FormErrors<LinksStepValues> = {};
  const check = (field: keyof LinksStepValues) => {
    const raw = v[field]?.trim();
    if (!raw) return;
    try {
      // Accept only http(s); anything else (mailto:, javascript:) is rejected
      const u = new URL(raw);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') {
        errors[field] = 'URL must start with http:// or https://';
      }
    } catch {
      errors[field] = 'Invalid URL';
    }
  };
  check('sharepointLink');
  check('teamsLink');
  check('customerWebsite');
  return errors;
}

// ─── Tab visibility ──────────────────────────────────────────────────

export type ProjectTabKey = 'overview' | 'finance' | 'kronos' | 'milestones' | 'avantProjet';

export interface ProjectTab {
  key: ProjectTabKey;
  path: string;
}

/**
 * Pure helper reused by the per-project tab bar AND (in principle) by the
 * Sidebar's deep-links. Mirrors the spec §4 "Inside a Project" visibility
 * matrix:
 *
 * - Overview: always visible to any authenticated user (reads are open)
 * - Finance: visible to `finance`, `direction`, `admin`, OR the project's
 *   managers. Note: Finance data in rules is governed by the same roles,
 *   so this is UI-level parity rather than additional enforcement.
 * - Kronos (per-project tab): visible to ALL authenticated employees.
 *   The KronosSummaryPanel shows budget/progress stats (hoursSold/hoursSpent)
 *   readable by any employee; time entries are still scoped to own user by
 *   rules, so unassigned employees see an empty list there.
 * - Milestones: gated on assignment — only project members/managers and
 *   auto-assigned roles (finance, rd_manager, direction, quality, admin).
 */
export function visibleTabs(user: User, project: Project): ProjectTab[] {
  const tabs: ProjectTab[] = [{ key: 'overview', path: '' }];

  const isManager = project.team?.managers?.includes(user.uid) ?? false;
  const hasRole = (r: Role) => user.roles.includes(r);
  const financeVisible =
    hasRole('finance') || hasRole('direction') || hasRole('admin') || isManager;
  if (financeVisible) {
    tabs.push({ key: 'finance', path: 'finance' });
  }

  // Kronos: visible to all authenticated users (budget stats visible to everyone).
  tabs.push({ key: 'kronos', path: 'kronos' });

  // Milestones: only for assigned members (team + auto-assigned roles).
  const assigned = isAssignedToMe(user, project);
  if (assigned) {
    tabs.push({ key: 'milestones', path: 'milestones' });
  }

  // Avant-projet : visible dès que la clé existe, pour qui voit déjà les jalons.
  // La PRÉSENCE de la clé est la case cochée — pas de booléen à interroger.
  if (assigned && project.avantProjet) {
    tabs.push({ key: 'avantProjet', path: 'avant-projet' });
  }

  return tabs;
}
